import React, { useEffect, useState } from 'react';
import { Col, Progress, Row, Space, Spin } from 'antd';
import { Modal, Form, Input, Radio, Slider, Button, Select, InputNumber, Table, message } from '@maxtropy/components';
import {
  apiV2MicrogridResolvedAddStrategyPost,
  apiV2MicrogridResolvedEditStrategyPost,
  apiV2MicrogridResolvedGetStrategyPost,
  apiV2MicrogridResolvedGetStrategyObjectListPost,
  apiV2MicrogridResolvedGetPredictModelListPost,
} from '@maxtropy/intelligent-dispath-apis-v2';
import { useRequest } from 'ahooks';
import {
  IndicatorEnum,
  IndicatorEnumDisplay,
  PreSetStrategyParamEnumDisplay,
  PreSetStrategyParamEnum,
  StrategyTypeEnum,
  StrategyTypeEnumDisplay,
} from '../util';
import styles from './index.module.scss';
import { SliderMarks } from 'antd/es/slider';
import { InfoCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { isEmpty, isNil, isString, uniqueId } from 'lodash-es';

const marks: SliderMarks = {
  0: 0,
  20: 20,
  40: 40,
  60: 60,
  80: 80,
  100: 100,
};

interface Props {
  strategyId?: number;
  configId?: string;
  onConfirm?: () => void;
  onCancel?: () => void;
}

const checkUnique = (list: any[]) => {
  const seen = new Set<string>();
  let isFlag: boolean = false;

  list.forEach(item => {
    const key = `${item.microGridObjectKey}-${item.measurementType}`;
    if (seen.has(key)) {
      isFlag = true;
    } else {
      seen.add(key);
    }
  });

  return isFlag;
};

const CreateStrategyModal = ({ onCancel, onConfirm, strategyId, configId }: Props) => {
  const [form] = Form.useForm();
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [predictModelCache, setPredictModelCache] = useState<Record<string, any[]>>({});

  const strategyType: StrategyTypeEnum = Form.useWatch('strategyType', form);
  const preSetStrategyParam: PreSetStrategyParamEnum = Form.useWatch('preSetStrategyParam', form);
  const powerSupplyWeight: number = Form.useWatch('powerSupplyWeight', form);
  const electricityCostWeight: number = Form.useWatch('electricityCostWeight', form) || 0;
  const greenPowerWeight: number = Form.useWatch('greenPowerWeight', form) || 0;
  const modelConfigList = Form.useWatch('modelConfigList', form) || 0;

  const { loading } = useRequest(
    async () => {
      return apiV2MicrogridResolvedGetStrategyPost({
        strategyId,
      });
    },
    {
      onSuccess: (res) => {
        if (res) {
          form.setFieldsValue({
            ...res,
            modelConfigList: res.modelConfigList?.map(item => ({
              ...item,
              microGridObjectKey: `${item.microGridObjectType}_${item.microGridObjectId}`,
            }) ),
          });
        }
      },
      ready: !!strategyId,
      refreshDeps: [strategyId],
    }
  );

  const { data: strategyObjectList } = useRequest(
    async () => {
      return apiV2MicrogridResolvedGetStrategyObjectListPost({
        configId,
      }).then(res => res?.list ?? []);
    },
    {
      ready: !!configId,
      refreshDeps: [configId],
    }
  );

  useEffect(() => {
    if (!strategyId && (strategyObjectList ?? []).length > 0) {
      // 新增策略策略对象接口返回的数据做默认值
      form.setFieldsValue({
        modelConfigList: strategyObjectList!.map(item => ({
          microGridObjectKey: `${item.objectType}_${item.objectId}`,
          microGridObjectId: item.objectId,
          microGridObjectType: item.objectType,
          microGridObjectName: item.objectName,
          measurementType: IndicatorEnum.CUMULATE,
          id: uniqueId(),
          isAdd: true,
        })),
      });
    }
  }, [form, strategyId, strategyObjectList]);

  useEffect(() => {
    const fetchPredictModels = async () => {
      if (isEmpty(modelConfigList)) return;
      const needFetch = new Set<string>();
      modelConfigList.forEach((item: any) => {
        const { microGridObjectId, microGridObjectType, measurementType } = item;
        if (
          microGridObjectId &&
          !isNil(microGridObjectType) &&
          !isNil(measurementType) &&
          !predictModelCache[`${microGridObjectId}-${microGridObjectType}-${measurementType}`]
        ) {
          needFetch.add(`${microGridObjectId}-${microGridObjectType}-${measurementType}`);
        }
      });

      if (needFetch.size === 0) return;

      await Promise.all(
        Array.from(needFetch).map(async cacheKey => {
          const [objectId, modelType, measurementType] = cacheKey.split('-');
          const res = await apiV2MicrogridResolvedGetPredictModelListPost({
            objectId: Number(objectId),
            modelType: Number(modelType),
            measurementType: Number(measurementType),
          });
          setPredictModelCache(prev => ({
            ...prev,
            [`${objectId}-${modelType}-${measurementType}`]: res?.list ?? [],
          }));
        })
      );
    };

    fetchPredictModels();
  }, [modelConfigList, predictModelCache]);

  const getPredictOptions = ({ measurementType, microGridObjectId, microGridObjectType }: any) => {
    if (!microGridObjectId || !measurementType) return [];
    const cacheKey = `${microGridObjectId}-${microGridObjectType}-${measurementType}`;
    return (
      predictModelCache[cacheKey]?.map(item => ({
        label: item.modelName,
        value: item.id,
      })) || []
    );
  };

  const validateWeights = () => {
    if (strategyType === StrategyTypeEnum.CUSTOM) {
      const sum = Number(powerSupplyWeight) + Number(electricityCostWeight) + Number(greenPowerWeight);
      if (sum !== 100) {
        return Promise.reject(new Error('三个权重的总和必须为100%'));
      }
    }
    return Promise.resolve();
  };

  const onOk = () => {
    form.validateFields().then(() => {
      const { modelConfigList, ...rest } = form.getFieldsValue(true);

      const isFlag = checkUnique(modelConfigList);
      if (isFlag) {
        message.error('同一对象下不能选择多个累积量或瞬时量');
        return;
      }

      const newModelConfigList = modelConfigList.map((item: any) => {
        const { id, microGridObjectKey, ...restItem } = item;
        return { ...restItem, id: item.isAdd ? undefined : id, isAdd: undefined };
      });

      setConfirmLoading(true);
      const api = strategyId ? apiV2MicrogridResolvedEditStrategyPost : apiV2MicrogridResolvedAddStrategyPost;
      api({
        ...rest,
        configId,
        modelConfigList: newModelConfigList,
      })
        .then(onConfirm)
        .finally(() => setConfirmLoading(false));
    });
  };

  const columns = [
    {
      title: (<div className={styles.requiredTitle}>微电网对象</div>),
      dataIndex: 'microGridObjectKey',
      render: (_: number, record: any, index: number) => {
        return (
          <Form.Item
            name={['modelConfigList', index, 'microGridObjectKey']}
            style={{ marginBottom: 0 }}
            rules={[{ message: '请选择', required: true }]}
          >
            <Select
              allowClear
              placeholder="请选择"
              onChange={(value, options: any) => {
                const tableData = form.getFieldValue('modelConfigList');
                tableData[index] = {
                  ...tableData[index],
                   microGridObjectId: options.id,
                  microGridObjectType: options?.type,
                  microGridObjectName: options?.label,
                  predictModelId: undefined,
                };
              }}
              options={(strategyObjectList ?? []).map(item => ({
                label: item.objectName,
                value: `${item.objectType}_${item.objectId}`,
                type: item.objectType,
                id: item.objectId,
              }))}
            />
          </Form.Item>
        );
      },
    },
    {
      title: (<div className={styles.requiredTitle}>预测模型数据属性类型</div>),
      dataIndex: 'measurementType',
      render: (_: number, record: any, index: number) => {
        return (
          <Form.Item
            name={['modelConfigList', index, 'measurementType']}
            style={{ marginBottom: 0 }}
            rules={[{ message: '请选择', required: true }]}
          >
            <Select
              allowClear
              placeholder="请选择"
              onChange={(value, options: any) => {
                const tableData = form.getFieldValue('modelConfigList');
                tableData[index] = {
                  ...tableData[index],
                  predictModelId: undefined,
                };
              }}
              options={Object.entries(IndicatorEnumDisplay).map(([k, v]) => ({ label: v, value: Number(k) }))}
            />
          </Form.Item>
        );
      },
    },
    {
      title: '预测模型',
      dataIndex: 'predictModelId',
      render: (_: number, record: any, index: number) => {
        const { measurementType, microGridObjectId, microGridObjectType } = record;
        return (
          <Form.Item
            name={['modelConfigList', index, 'predictModelId']}
            style={{ marginBottom: 0 }}
            rules={measurementType == IndicatorEnum.CUMULATE ? [{ message: '请选择', required: true }] : []}
          >
            <Select
              allowClear
              placeholder="请选择"
              options={getPredictOptions({ measurementType, microGridObjectId, microGridObjectType })}
            />
          </Form.Item>
        );
      },
    },
    {
      title: '操作',
      dataIndex: 'id',
      width: 120,
      render: (id: number) => {
        return (
          <Button
            type="link"
            danger
            onClick={() => {
              const tableData = form.getFieldValue('modelConfigList');
              const newData = tableData.filter((item: any) => item.id !== id);
              form.setFieldsValue({ modelConfigList: newData });
            }}
          >
            删除
          </Button>
        );
      },
    },
  ];

  const onSliderChange = (name: string, value: string | number | null) => {
    form.setFields([{ name, value: value ? (isString(value) ? Number(value) : value) : 0 }]);
  };

  const addRow = () => {
    const tableData = form.getFieldValue('modelConfigList');
    const newRow = {
      id: uniqueId(),
      microGridObjectType: undefined,
      microGridObjectName: undefined,
      isAdd: true,
    };
    form.setFieldsValue({ modelConfigList: [...tableData, newRow] });
  };

  return (
    <Modal
      title={`${strategyId ? '编辑' : '新建'}策略`}
      size="big"
      contentClassName="modal-form-content"
      open={true}
      bodyScroll
      onOk={onOk}
      confirmLoading={confirmLoading}
      onCancel={onCancel}
    >
      <Spin spinning={strategyId ? loading : false}>
        <Form form={form} className={styles.form} initialValues={{ powerSupplyWeight: 100 }}>
          <Row>
            <Col span={24}>
              <Form.Item
                label="策略名称"
                name="strategyName"
                rules={[
                  { required: true, message: '请输入策略名称' },
                  {
                    max: 50,
                    message: '最多输入50个字符',
                  },
                ]}
              >
                <Input placeholder="请输入名称" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                label="策略类型"
                name="strategyType"
                rules={[{ required: true, message: '请选择策略类型' }]}
                initialValue={StrategyTypeEnum.PRESET}
              >
                <Radio.Group
                  onChange={e => {
                    if (e.target.value === StrategyTypeEnum.CUSTOM) {
                      form.setFieldsValue({ preSetStrategyParam: undefined });
                    } else {
                      form.setFieldsValue({
                        preSetStrategyParam: PreSetStrategyParamEnum.SUPPLY_GUARANTEE_OPTIMIZATION,
                        powerSupplyWeight: 100,
                        electricityCostWeight: 0,
                        greenPowerWeight: 0,
                      });
                    }
                  }}
                >
                  <Radio value={StrategyTypeEnum.PRESET}>{StrategyTypeEnumDisplay[StrategyTypeEnum.PRESET]}</Radio>
                  <Radio value={StrategyTypeEnum.CUSTOM}>{StrategyTypeEnumDisplay[StrategyTypeEnum.CUSTOM]}</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
            {strategyType === StrategyTypeEnum.PRESET && (
              <Col span={24}>
                <Form.Item
                  label="策略参数"
                  name="preSetStrategyParam"
                  rules={[{ required: true, message: '请选择策略参数' }]}
                  initialValue={PreSetStrategyParamEnum.SUPPLY_GUARANTEE_OPTIMIZATION}
                >
                  <Select
                    allowClear
                    placeholder="请选择"
                    options={Object.entries(PreSetStrategyParamEnumDisplay).map(([k, v]) => ({
                      label: v,
                      value: Number(k),
                    }))}
                    onChange={(value: PreSetStrategyParamEnum) => {
                      form.setFieldsValue({
                        powerSupplyWeight: value === PreSetStrategyParamEnum.SUPPLY_GUARANTEE_OPTIMIZATION ? 100 : 0,
                        electricityCostWeight: value === PreSetStrategyParamEnum.COST_OPTIMIZATION ? 100 : 0,
                        greenPowerWeight: value === PreSetStrategyParamEnum.GREEN_POWER_DISPOSAL_OPTIMIZATION ? 100 : 0,
                      });
                    }}
                  />
                </Form.Item>
                <Space
                  style={{ margin: '0 0 24px 136px', display: 'flex', maxWidth: 464 }}
                  direction="vertical"
                  size={24}
                >
                  <div className={styles.progressItem}>
                    <span>{`${
                      PreSetStrategyParamEnumDisplay[PreSetStrategyParamEnum.SUPPLY_GUARANTEE_OPTIMIZATION]
                    }权重:`}</span>
                    <Progress
                      percent={preSetStrategyParam === PreSetStrategyParamEnum.SUPPLY_GUARANTEE_OPTIMIZATION ? 100 : 0}
                      size={{ height: 4 }}
                      status="active"
                      strokeColor={{ from: '#51A1FF', to: '#51DBFF' }}
                    />
                  </div>
                  <div className={styles.progressItem}>
                    <span>{`${PreSetStrategyParamEnumDisplay[PreSetStrategyParamEnum.COST_OPTIMIZATION]}权重:`}</span>
                    <Progress
                      percent={preSetStrategyParam === PreSetStrategyParamEnum.COST_OPTIMIZATION ? 100 : 0}
                      status="active"
                      size={{ height: 4 }}
                      strokeColor={{ from: '#51A1FF', to: '#51DBFF' }}
                    />
                  </div>
                  <div className={styles.progressItem}>
                    <span>{`${
                      PreSetStrategyParamEnumDisplay[PreSetStrategyParamEnum.GREEN_POWER_DISPOSAL_OPTIMIZATION]
                    }权重:`}</span>
                    <Progress
                      percent={
                        preSetStrategyParam === PreSetStrategyParamEnum.GREEN_POWER_DISPOSAL_OPTIMIZATION ? 100 : 0
                      }
                      status="active"
                      size={{ height: 4 }}
                      strokeColor={{ from: '#51A1FF', to: '#51DBFF' }}
                    />
                  </div>
                </Space>
              </Col>
            )}
            {strategyType === StrategyTypeEnum.CUSTOM && (
              <Col span={24}>
                <Form.Item
                  label="策略参数"
                  name="preSetStrategyParam"
                  dependencies={['powerSupplyWeight', 'electricityCostWeight', 'greenPowerWeight']}
                  required
                  rules={[
                    form => ({
                      validator: validateWeights,
                    }),
                  ]}
                >
                  <Form.Item
                    label={`${
                      PreSetStrategyParamEnumDisplay[PreSetStrategyParamEnum.SUPPLY_GUARANTEE_OPTIMIZATION]
                    }权重`}
                    name="powerSupplyWeight"
                  >
                    <Row gutter={8}>
                      <Col span={20}>
                        <Slider
                          min={0}
                          max={100}
                          marks={marks}
                          onChange={value => onSliderChange('powerSupplyWeight', value)}
                          value={powerSupplyWeight ?? 0}
                        />
                      </Col>
                      <Col span={4}>
                        <InputNumber
                          min={0}
                          max={100}
                          step={1}
                          precision={0}
                          suffix="%"
                          value={powerSupplyWeight}
                          onChange={value => onSliderChange('powerSupplyWeight', value)}
                        />
                      </Col>
                    </Row>
                  </Form.Item>
                  <Form.Item
                    label={`${PreSetStrategyParamEnumDisplay[PreSetStrategyParamEnum.COST_OPTIMIZATION]}权重`}
                    name="electricityCostWeight"
                  >
                    <Row gutter={8}>
                      <Col span={20}>
                        <Slider
                          min={0}
                          max={100}
                          marks={marks}
                          onChange={value => onSliderChange('electricityCostWeight', value)}
                          value={electricityCostWeight ?? 0}
                        />
                      </Col>
                      <Col span={4}>
                        <InputNumber
                          min={0}
                          max={100}
                          suffix="%"
                          step={1}
                          precision={0}
                          value={electricityCostWeight}
                          onChange={value => onSliderChange('electricityCostWeight', value)}
                        />
                      </Col>
                    </Row>
                  </Form.Item>
                  <Form.Item
                    label={`${
                      PreSetStrategyParamEnumDisplay[PreSetStrategyParamEnum.GREEN_POWER_DISPOSAL_OPTIMIZATION]
                    }权重`}
                    name="greenPowerWeight"
                  >
                    <Row gutter={8}>
                      <Col span={20}>
                        <Slider
                          min={0}
                          max={100}
                          marks={marks}
                          onChange={value => onSliderChange('greenPowerWeight', value)}
                          value={greenPowerWeight ?? 0}
                        />
                      </Col>
                      <Col span={4}>
                        <InputNumber
                          min={0}
                          max={100}
                          suffix="%"
                          step={1}
                          precision={0}
                          value={greenPowerWeight}
                          onChange={value => onSliderChange('greenPowerWeight', value)}
                        />
                      </Col>
                    </Row>
                  </Form.Item>
                </Form.Item>
              </Col>
            )}
          </Row>
          <div className={styles.modelTitle}>
            模型应用
            <span>
              <InfoCircleOutlined style={{ fontSize: '14px', marginRight: 4, color: 'var(--mx-warning-color)' }} />
              光伏、风电、负载预测模型的预测数据模型类型要与策略求解时任务颗粒度一致，且预测范围要大于24小时
            </span>
          </div>
          <Form.Item noStyle labelAlign="left" name="modelConfigList" valuePropName="dataSource">
            <Table columns={columns} />
          </Form.Item>
          <Button wrapClassName={styles.addBtn} type="dashed" onClick={addRow} ghost icon={<PlusOutlined />}>
            选择模型
          </Button>
        </Form>
      </Spin>
    </Modal>
  );
};

export default CreateStrategyModal;
